/**
 * Live-editable property rows for an ad-hoc scope ITEM's scope_meta - the Scope status (a colored
 * single-select, like Stakeholder Sentiment) and the Activities / work type (a multi-select, like
 * Geographies of Operation), plus the read-only estimate Hours. Rendered by the `propertyRow` node
 * view for a `scopemeta:<primaryFieldKey>:<metaKey>` row (see DealScopePane.renderRow), so the item
 * body is one unified 2-column property layout with the scope-brief rows below.
 *
 * Edits live-save via `commitAdhocScopeFields` (present for non-viewers); with no committer the row
 * is read-only. The controls reuse the shared `.scope-prop-*` classes; the status pills reuse the
 * Scope-doc chip colors (`.rte-scope-chip--{variant}`) so the option colors match the read-only chip.
 */

import * as React from 'react';
import {
  SCOPE_BRIEF,
  SCOPE_STATUS_VALUES,
  WORK_TYPE_OPTIONS,
  workTypeLabel,
  type ScopeMeta,
  type ScopeStatus,
} from '@protoss/shared';
import { CheckIcon, Cross1Icon, PlusIcon, Popover } from '@protoss/ui';

import { scopeStatusChip } from '../utils/scope-doc';

/** The scope-brief TAG dimensions edited as creatable multi-selects (arrays of free-text tags). */
export type ScopeTagKey = 'inScope' | 'outOfScope' | 'complexityDrivers' | 'assumptions';
/** scope_meta keys the item property panel edits live: status, activities, and the tag dimensions. */
export type ScopeItemMetaKey = 'scopeStatus' | 'workType' | ScopeTagKey;

function metaLabel(metaKey: ScopeItemMetaKey): string {
  if (metaKey === 'scopeStatus') return 'Scope';
  if (metaKey === 'workType') return 'Activities';
  return SCOPE_BRIEF.find((d) => d.key === metaKey)?.label ?? metaKey;
}

/** Colored status pill - same label + color as the read-only Scope-doc chip (green / amber / grey). */
function statusPill(status: ScopeStatus | undefined): React.ReactElement {
  const chip = scopeStatusChip(status ? ({ scopeStatus: status } as ScopeMeta) : undefined);
  return <span className={`rte-scope-chip rte-scope-chip--${chip.variant}`}>{chip.label}</span>;
}

function ScopeStatusControl({
  status,
  onChange,
}: {
  status: ScopeStatus;
  onChange: (s: ScopeStatus) => void;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="scope-prop-trigger">
          {statusPill(status)}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="dropdown-content scope-prop-menu"
          align="start"
          sideOffset={4}
          collisionPadding={8}
        >
          {SCOPE_STATUS_VALUES.map((v) => (
            <button
              key={v}
              type="button"
              className="scope-prop-menu__item"
              onClick={() => {
                onChange(v);
                setOpen(false);
              }}
            >
              {statusPill(v)}
              {v === status ? <CheckIcon className="scope-prop-menu__check" /> : null}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function ScopeWorkTypeControl({
  values,
  onChange,
}: {
  values: string[];
  onChange: (v: string[]) => void;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const toggle = (v: string) =>
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <div className="scope-prop-pills">
      {values.map((v) => (
        <span key={v} className="scope-prop-pill scope-prop-pill--removable">
          {workTypeLabel(v)}
          <button
            type="button"
            className="scope-prop-pill__remove"
            aria-label={`Remove ${workTypeLabel(v)}`}
            onClick={() => onChange(values.filter((x) => x !== v))}
          >
            <Cross1Icon />
          </button>
        </span>
      ))}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button type="button" className="scope-prop-choose">
            <PlusIcon className="scope-prop-choose__icon" />
            Choose
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="dropdown-content scope-prop-menu"
            align="start"
            sideOffset={4}
            collisionPadding={8}
          >
            {WORK_TYPE_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className="scope-prop-menu__item"
                onClick={() => toggle(o.value)}
              >
                <span className="scope-prop-pill">{o.label}</span>
                {values.includes(o.value) ? (
                  <CheckIcon className="scope-prop-menu__check" />
                ) : null}
              </button>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

/**
 * Creatable multi-select for a scope-brief tag dimension - the same pattern as the property panel's
 * MultiSelectControl (Geographies): chips with x inline, then an "+ Add" popover holding a
 * type-to-create input, the suggestion list (checkable - suggestions are the deal-wide reuse pool),
 * and a Create row for free text. Suggestions live INSIDE the menu, not as an always-visible cloud.
 */
function ScopeTagControl({
  values,
  suggestions,
  onChange,
}: {
  values: string[];
  suggestions: string[];
  onChange: (v: string[]) => void;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const remove = (v: string) => onChange(values.filter((x) => x !== v));
  const toggle = (v: string) =>
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  // Menu options: the suggestion pool plus the current values (so a selected tag always shows its
  // check even when the pool misses it).
  const options = [...new Set([...suggestions, ...values])];
  const trimmed = query.replace(/\s+/g, ' ').trim();
  const filtered = trimmed
    ? options.filter((o) => o.toLowerCase().includes(trimmed.toLowerCase()))
    : options;
  const exact = options.find((o) => o.toLowerCase() === trimmed.toLowerCase());
  const canCreate = !!trimmed && !exact;
  const submit = () => {
    if (!trimmed) return;
    if (exact) {
      if (!values.includes(exact)) onChange([...values, exact]);
    } else {
      onChange([...values, trimmed]);
    }
    setQuery('');
  };
  return (
    <div className="scope-prop-pills">
      {values.map((v) => (
        <span key={v} className="scope-prop-pill scope-prop-pill--removable">
          {v}
          <button
            type="button"
            className="scope-prop-pill__remove"
            aria-label={`Remove ${v}`}
            onClick={() => remove(v)}
          >
            <Cross1Icon />
          </button>
        </span>
      ))}
      <Popover.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
      >
        <Popover.Trigger asChild>
          <button type="button" className="scope-prop-choose">
            <PlusIcon className="scope-prop-choose__icon" />
            {values.length === 0 ? 'Add' : null}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="dropdown-content scope-prop-menu"
            align="start"
            sideOffset={4}
            collisionPadding={8}
          >
            <input
              className="scope-prop-menu__search"
              autoFocus
              value={query}
              placeholder="Type to add or search…"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
            <div className="scope-prop-menu__list">
              {filtered.map((o) => (
                <button
                  key={o}
                  type="button"
                  className="scope-prop-menu__item"
                  onClick={() => toggle(o)}
                >
                  <span className="scope-prop-pill">{o}</span>
                  {values.includes(o) ? <CheckIcon className="scope-prop-menu__check" /> : null}
                </button>
              ))}
              {canCreate ? (
                <button type="button" className="scope-prop-menu__create" onClick={submit}>
                  <PlusIcon className="scope-prop-menu__create-icon" />
                  Create &ldquo;{trimmed}&rdquo;
                </button>
              ) : null}
              {filtered.length === 0 && !canCreate ? (
                <span className="scope-prop-menu__empty">Type to add</span>
              ) : null}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

export function ScopeItemMetaRow({
  metaKey,
  scope,
  suggestions = [],
  onCommit,
}: {
  metaKey: ScopeItemMetaKey;
  scope: ScopeMeta | undefined;
  /** Tag dimensions only: clickable suggestion values (existing tags on the deal, driver library …). */
  suggestions?: string[];
  onCommit: ((patch: Partial<Pick<ScopeMeta, ScopeItemMetaKey>>) => void) | undefined;
}): React.ReactElement {
  let value: React.ReactNode;
  if (metaKey === 'scopeStatus') {
    const status = scope?.scopeStatus ?? 'requested';
    value = onCommit ? (
      <ScopeStatusControl status={status} onChange={(s) => onCommit({ scopeStatus: s })} />
    ) : (
      statusPill(status)
    );
  } else if (metaKey === 'workType') {
    const values = (scope?.workType ?? []) as string[];
    value = onCommit ? (
      <ScopeWorkTypeControl
        values={values}
        onChange={(v) => onCommit({ workType: v as ScopeMeta['workType'] })}
      />
    ) : values.length > 0 ? (
      <span className="scope-prop-readonly">{workTypeLabel(values)}</span>
    ) : (
      <span className="scope-prop-value__empty">Empty</span>
    );
  } else {
    // Tag dimension (inScope / outOfScope / complexityDrivers / assumptions).
    const values = (scope?.[metaKey] ?? []) as string[];
    value = onCommit ? (
      <ScopeTagControl
        values={values}
        suggestions={suggestions}
        onChange={(v) =>
          onCommit({ [metaKey]: v } as Partial<Pick<ScopeMeta, ScopeItemMetaKey>>)
        }
      />
    ) : values.length > 0 ? (
      <div className="scope-prop-pills">
        {values.map((v) => (
          <span key={v} className="scope-prop-pill">
            {v}
          </span>
        ))}
      </div>
    ) : (
      <span className="scope-prop-value__empty">Empty</span>
    );
  }
  return (
    <>
      <div className="scope-prop-row__label">
        <span className="scope-prop-row__label-text">{metaLabel(metaKey)}</span>
      </div>
      <div className="scope-prop-row__value">{value}</div>
    </>
  );
}
